import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
  skipToken,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdMaterialTypeahead,
  GetV1CompaniesCompanyIdMaterialTypeaheadData,
  getV1CompaniesCompanyIdProjectsTypeahead,
  GetV1CompaniesCompanyIdProjectsTypeaheadData,
  getV1CompaniesCompanyIdSiteTypeahead,
  ProjectState,
} from '@treadinc/horizon-api-spec';
import { useCallback, useMemo, useState } from 'react';

import {
  FilterMenuItemLoadingReason,
  FilterMenuOption,
} from '~components/Filters/FilterMenuItem';
import { toMaterialTypeaheadOption } from '~hooks/useMaterials/typeaheadOption';
import { ProjectTypeahead } from '~hooks/useProjects/models';
import { SiteTypeahead } from '~hooks/useSites/models';
import { extractPagination, Pagination } from '~services/pagination';

const QUERY_PAGINATION_LIMIT = 10;

export interface MaterialRatesFilterQuery {
  clear: () => void;
  data: FilterMenuOption[];
  fetch: (searchQuery: string) => void;
  fetchMore:
    | ((
        options?: FetchNextPageOptions | undefined,
      ) => Promise<
        InfiniteQueryObserverResult<InfiniteData<FilterMenuOption[], unknown>, Error>
      >)
    | undefined;
  loadingReason?: FilterMenuItemLoadingReason;
}

type QueryParamsState = {
  shouldFetch: boolean;
  filters: { query: string };
  pagination?: Pagination & { hasMore: boolean };
};

type SimpleQueryParams = Pick<
  NonNullable<GetV1CompaniesCompanyIdMaterialTypeaheadData['query']>,
  'page[after]' | 'page[limit]' | 'search[query]'
>;

function deriveSimpleQueryParams(queryParams: QueryParamsState) {
  const { filters, pagination } = queryParams;

  const query: SimpleQueryParams = { 'page[limit]': QUERY_PAGINATION_LIMIT };

  if (pagination?.after) {
    query['page[after]'] = pagination.after;
  }

  if (filters.query.trim().length) {
    query['search[query]'] = filters.query.trim();
  }

  return query;
}

/**
 * Materials
 */
export function useMaterialsQuery(companyId: string): MaterialRatesFilterQuery {
  const queryKey = 'materialRatesFiltersQueries_getV1CompaniesCompanyIdMaterialTypeahead';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query = deriveSimpleQueryParams({
            ...queryParams,
            pagination: pageParam,
          });

          return getV1CompaniesCompanyIdMaterialTypeahead({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const materials = response.data.data.map(toMaterialTypeaheadOption);
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return materials;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Sites
 */
export function useSitesQuery(companyId: string): MaterialRatesFilterQuery {
  const queryKey = 'materialRatesFiltersQueries_getV1CompaniesCompanyIdSiteTypeahead';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query = deriveSimpleQueryParams({
            ...queryParams,
            pagination: pageParam,
          });

          return getV1CompaniesCompanyIdSiteTypeahead({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const sites = response.data.data.map((site) => {
              const parsed = SiteTypeahead.parse(site);

              return { label: parsed.name, value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return sites;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}

/**
 * Projects
 */
function deriveProjectQueryParams(queryParams: QueryParamsState, accountId: string) {
  const query: GetV1CompaniesCompanyIdProjectsTypeaheadData['query'] =
    deriveSimpleQueryParams(queryParams);
  query['filter[states]'] = [ProjectState.ACTIVE];
  query['filter[account_id]'] = accountId;

  return query;
}

export function useProjectsQuery(
  companyId: string,
  accountId: string,
): MaterialRatesFilterQuery {
  const queryKey = 'materialRatesFiltersQueries_getV1CompaniesCompanyIdProjectsTypeahead';
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? ({ pageParam, signal }) => {
          const query = deriveProjectQueryParams(
            {
              ...queryParams,
              pagination: pageParam,
            },
            accountId,
          );

          return getV1CompaniesCompanyIdProjectsTypeahead({
            path: { 'company-id': companyId },
            query,
            signal,
          }).then((response) => {
            const projects = response.data.data.map((project) => {
              const parsed = ProjectTypeahead.parse(project);

              return { label: parsed.name, value: parsed.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return projects;
          });
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const loadingReason = useMemo(() => {
    if (isFetchingNextPage) {
      return FilterMenuItemLoadingReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return FilterMenuItemLoadingReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, []);

  return { clear, data: allData, fetch, fetchMore, loadingReason };
}
